import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useAuth0Integration } from '../contexts/Auth0Context';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Building2, Mail, Lock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [auth0EnabledSetting, setAuth0EnabledSetting] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const { isAuth0Configured, loginWithAuth0, isLoading: auth0Loading, isAuthenticated: isAuth0Authenticated, syncing } = useAuth0Integration();
  const navigate = useNavigate();

  // Fetch Auth0 enabled setting
  useEffect(() => {
    const fetchAuth0Setting = async () => {
      try {
        const response = await axios.get(`${API_URL}/settings/auth0`);
        setAuth0EnabledSetting(response.data.auth0_enabled);
      } catch (error) {
        console.error('Error fetching Auth0 setting:', error);
        setAuth0EnabledSetting(false);
      }
    };
    fetchAuth0Setting();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Show syncing state for Auth0
  useEffect(() => {
    if (syncing) {
      toast.info('Synchronisation avec Auth0...');
    }
  }, [syncing]);

  // Debug Auth0 state
  useEffect(() => {
    console.log('Auth0 state:', { isAuth0Configured, isAuth0Authenticated, auth0Loading, syncing, auth0EnabledSetting });
  }, [isAuth0Configured, isAuth0Authenticated, auth0Loading, syncing, auth0EnabledSetting]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      toast.success('Connexion réussie!');
      navigate('/dashboard');
    } else {
      toast.error(result.error || 'Échec de la connexion');
    }

    setLoading(false);
  };

  const handleAuth0Login = () => {
    console.log('Auth0 login clicked');
    if (isAuth0Authenticated) {
      console.log('Already authenticated with Auth0, forcing re-login...');
      // Clear any existing session and re-authenticate
      localStorage.removeItem('token');
    }
    loginWithAuth0({
      appState: { returnTo: '/dashboard' }
    });
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-slate-50"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1765046255500-ce203b84c307?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
      data-testid="login-page"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/90 to-slate-900/90"></div>
      
      <Card className="relative z-10 w-full max-w-md border-slate-200 shadow-xl" data-testid="login-card">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-700">
            <Building2 className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            B2RCS
          </CardTitle>
          <CardDescription>Connectez-vous à votre compte</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-slate-700">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12 rounded-xl border-slate-200 bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-slate-700">
                Mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-12 rounded-xl border-slate-200 bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-indigo-700 font-semibold shadow-lg hover:shadow-xl hover:bg-indigo-800 transition-all hover:-translate-y-0.5"
              data-testid="login-button"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>

            {isAuth0Configured && auth0EnabledSetting && (
              <>
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-4 text-slate-500">Ou continuer avec</span>
                  </div>
                </div>

                <Button
                  type="button"
                  onClick={handleAuth0Login}
                  disabled={auth0Loading}
                  variant="outline"
                  className="w-full h-12 rounded-full border-2 border-slate-200 font-semibold hover:bg-slate-50 transition-all"
                  data-testid="auth0-login-button"
                >
                  <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#EB5424"/>
                    <path d="M2 17L12 22L22 17M2 12L12 17L22 12" stroke="#EB5424" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {auth0Loading ? 'Connexion...' : 'Se connecter avec Auth0'}
                </Button>

                <p className="mt-4 text-center text-xs text-slate-500">
                  Utilisez vos identifiants B2SMS.com
                </p>
              </>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;